/**
 * GreetingHeader — concierge-style hero greeting strip at the top of the
 * borrower / end-consumer Home pages.
 *
 * Per the v3 design pass (Wave A Home dashboard, fixes H1):
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  THURSDAY, MAY 15                              [Upload] [New loan]│
 *   │  Good morning, Sarah                                              │
 *   │  3 active loans · $4.2M exposure · Next close 12 days             │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 *   - Date eyebrow: `Typography variant="overline"` (11px tracked)
 *   - Hero greeting: `Typography variant="h5"` (16px MD3 — the page-title
 *     ceiling per portals.md §11b2). NOT h1 — the page title h5 is the
 *     hierarchical max and this is the page title itself.
 *   - Inline stats row: 3 colored stat chips reusing the dashboard's
 *     existing computed `kpis` object so we don't recompute.
 *   - Right-aligned secondary actions: ghost "Upload docs" + filled "New loan".
 *
 * Time-of-day greeting key picks based on local hour. End-consumer caller
 * may pass a different `greetingKeyOverride` if its copy diverges.
 */

import ClockIcon from '@mui/icons-material/AccessTime';
import { Box, GlassButton, iconSizes, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export interface GreetingHeaderStat {
  /** Stable key for the stat — used by React. */
  id: string;
  /** Pre-formatted human label (e.g. "3 active loans"). */
  label: string;
  /** Semantic palette color the stat should be tinted with. */
  tone: 'primary' | 'secondary' | 'warning';
  /** Optional icon (small clock for next-close, etc.). */
  showClockIcon?: boolean;
}

export interface GreetingHeaderProps {
  /** First name to interpolate into the greeting. */
  firstName: string;
  /** Inline stat chips rendered below the greeting. */
  stats?: GreetingHeaderStat[];
  /**
   * Override the greeting i18n key. Default selects from
   * `consumer.home.greeting.morning|afternoon|evening` based on local time.
   */
  greetingKeyOverride?: string;
  /** Override the secondary CTA label key (default "consumer.home.actions.uploadDocs"). */
  secondaryActionLabelKey?: string;
  /** Override the secondary CTA target path (default "/documents"). */
  secondaryActionPath?: string;
  /** Override the primary CTA label key (default "consumer.home.actions.newLoan"). */
  primaryActionLabelKey?: string;
  /** Override the primary CTA target path (default "/apply"). */
  primaryActionPath?: string;
}

/**
 * Select the time-of-day greeting key. Splits on Apple HIG / Google MD3 norms:
 *   - 04:00 – 11:59 → morning
 *   - 12:00 – 17:59 → afternoon
 *   - 18:00 – 03:59 → evening
 */
function selectGreetingKey(hour: number): string {
  if (hour >= 4 && hour < 12) return 'consumer.home.greeting.morning';
  if (hour >= 12 && hour < 18) return 'consumer.home.greeting.afternoon';
  return 'consumer.home.greeting.evening';
}

function formatDateEyebrow(date: Date): string {
  // Defaults to the user's locale; `Intl` falls back to en-US in test envs.
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch {
    return date.toDateString();
  }
}

function toneColor(tone: GreetingHeaderStat['tone']): string {
  switch (tone) {
    case 'primary':
      return 'primary.main';
    case 'warning':
      return 'warning.main';
    case 'secondary':
    default:
      return 'text.secondary';
  }
}

export function GreetingHeader({
  firstName,
  stats,
  greetingKeyOverride,
  secondaryActionLabelKey = 'consumer.home.actions.uploadDocs',
  secondaryActionPath = '/documents',
  primaryActionLabelKey = 'consumer.home.actions.newLoan',
  primaryActionPath = '/apply',
}: GreetingHeaderProps): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();

  const now = useMemo(() => new Date(), []);
  const greetingKey = greetingKeyOverride ?? selectGreetingKey(now.getHours());
  const dateLabel = useMemo(() => formatDateEyebrow(now), [now]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'flex-end' },
        justifyContent: 'space-between',
        gap: 2,
        pb: 1,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            color: 'text.secondary',
            letterSpacing: '0.12em',
            mb: 0.5,
          }}
        >
          {dateLabel}
        </Typography>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'text.primary',
            mb: stats && stats.length > 0 ? 1 : 0,
          }}
        >
          {t(greetingKey, { firstName })}
        </Typography>
        {stats && stats.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 1.5,
              rowGap: 0.5,
            }}
          >
            {stats.map((stat, idx) => (
              <Box
                key={stat.id}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {idx > 0 && (
                  <Typography component="span" variant="body2" aria-hidden sx={{ color: 'text.disabled' }}>
                    ·
                  </Typography>
                )}
                {stat.showClockIcon && (
                  <ClockIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: toneColor(stat.tone) }} />
                )}
                <Typography component="span" variant="body2" sx={{ color: toneColor(stat.tone), fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexShrink: 0,
          alignSelf: { xs: 'stretch', md: 'flex-end' },
        }}
      >
        <GlassButton variant="text" onClick={() => navigate(secondaryActionPath)} sx={{ minHeight: 44 }}>
          {t(secondaryActionLabelKey)}
        </GlassButton>
        <GlassButton
          variant="contained"
          color="primary"
          onClick={() => navigate(primaryActionPath)}
          sx={{ minHeight: 44 }}
        >
          {t(primaryActionLabelKey)}
        </GlassButton>
      </Box>
    </Box>
  );
}

export default GreetingHeader;
